import type { ReactNode } from 'react'

import type { CarReport } from '../api/types'
import { sendAffiliateClick } from '../api/usage'
import { useReportVisit } from '../api/useReportEngagement'
import { DAMAGE_REPORT_PARTNER, DAMAGE_REPORT_PROVIDER, damageReportUrl } from '../domain/damageReport'
import { Icon } from './Icon'

/**
 * The one thing on this page that earns anything.
 *
 * AutoChecken takes a commission on damage reports bought at `autoverleden.nl` by visitors who
 * arrive through this link, and there is no other revenue anywhere in the product. It is a card of
 * its own, high on the page, because a link nobody scrolls to earns nothing.
 *
 * **It is written as the honest answer to a real gap, not as an advertisement**, and that is the
 * commercial decision as much as the editorial one. The whole promise of this site is that it shows
 * what the RDW publishes and does not dress it up; a banner that read as sponsored content would
 * cost more trust than it could ever earn back. What it says is true and is nowhere else in the
 * report: the RDW does not publish damage history, so a car that has been written off and rebuilt
 * looks exactly like one that has never been touched, on every card above and below this one. A
 * reader who has just read that this vehicle's APK is valid and its odometer is logical is one
 * sentence away from asking the question this answers.
 *
 * **The plate is in the button.** "Bekijk het schadeverleden van S-038-BL" is a different
 * proposition from a bare "Check het nu!": it says the thing being bought is about *this*
 * car, which is what the reader is holding in their head at that moment. The mechanics of the link
 * itself — the `rel`, the `target` — are on `DamageReportLink` below, where both entry points share
 * them.
 *
 * There is deliberately **no affiliate disclosure line**. That is the owner's call, on the grounds
 * that no comparable kentekencheck carries one. Worth knowing if it ever comes back up:
 * `rel="sponsored"` is machine-readable and not a disclosure to a reader, and
 * the Reclamecode Social Media & Influencer Marketing does not cover this, but the general
 * unfair-commercial-practices rules on recognisable advertising do apply to a commissioned link.
 * That is a call for the business rather than for this file, and it is recorded here so the next
 * person does not have to rediscover the question.
 */
export function DamageHistorySection({ report }: { report: CarReport }) {
  return (
    <div className="damage">
      {/* Two sentences, because the subtitle carries the third, and one paragraph each so the gap
          the report has and the thing that fills it land on separate lines. A reader who has to work
          through a block of text to reach the button mostly does not reach the button. */}
      <p className="damage__lead">
        Een aanrijding of een herstelde total loss staat dus nergens in dit rapport, ook niet als die
        er is geweest.
      </p>

      <p className="damage__lead">
        {DAMAGE_REPORT_PROVIDER} heeft die gegevens wel: check het en voorkom een miskoop!
      </p>

      <DamageReportLink report={report} placement="schade">
        <Icon name="search" size={18} />
        <DamageCtaText plate={report.licensePlate} />
      </DamageReportLink>
    </div>
  )
}

/**
 * A second way in, from the APK card.
 *
 * The card above catches a reader at the top of the report; this catches the one who has just read
 * what this vehicle was pulled up on at its keuringen and is thinking about its condition — which is
 * the closest anyone on this page comes to being in the market for a damage report.
 *
 * **It carries the same button as the card above**, not an inline link. Two call-to-actions do not
 * read as a campaign here: they are thousands of pixels apart on a report this long, so no reader
 * ever sees both at once, and a quiet inline link competes with a page full of bold figures and
 * loses. What keeps this from reading as an advert
 * is the sentence above it, which is a genuinely surprising fact — people assume a valid APK means
 * a car has not been wrecked, and it means nothing of the sort.
 *
 * Two entry points is the ceiling. A third would tip the whole thing over; if another placement is
 * ever wanted, it should replace one of these rather than join them.
 */
export function DamageHistoryPrompt({ report }: { report: CarReport }) {
  return (
    <div className="damage-prompt">
      <p className="damage-prompt__text">
        Een APK zegt niets over schadeverleden. Een auto die zwaar beschadigd is geweest en netjes is
        hersteld, wordt gewoon goedgekeurd. Check het schadeverleden en voorkom een miskoop!
      </p>

      <DamageReportLink report={report} placement="apk">
        <Icon name="search" size={18} />
        <DamageCtaText plate={report.licensePlate} />
      </DamageReportLink>
    </div>
  )
}

/**
 * The link itself, in one place so both entry points cannot drift apart.
 *
 * `rel` is deliberately **not** `noreferrer`, unlike every other outbound link in this report:
 * copying that here strips the `Referer` header on the way to the partner. The commission is
 * attributed through the `site=` parameter in the URL rather than the header, so this is
 * belt-and-braces — but the cost of being wrong is invisible lost revenue, and `noopener` alone
 * gives the same security without touching the header. `sponsored` is the correct rel for a paid
 * link and costs nothing.
 *
 * `target="_blank"` so a visitor who buys a report still has this vehicle's own report behind them.
 *
 * **Every click is counted, by placement**, so each of the two entry points can be judged on what
 * it earns. The count is sent beside the navigation and never instead of it: there is no
 * `preventDefault`, no redirect through this site, and the `href` stays the partner's own URL — a
 * tracking hop would put this site's uptime in front of the commission. A middle click opens the
 * link too and is counted through `onAuxClick`. Nothing about the plate goes with the count.
 */
function DamageReportLink({
  report,
  placement,
  className = 'button damage__cta',
  children,
}: {
  report: CarReport
  /** Which entry point this is, matching the anchor id of the card it sits in. */
  placement: 'schade' | 'apk'
  className?: string
  children: ReactNode
}) {
  const { viewId } = useReportVisit()
  const recordClick = () => sendAffiliateClick(viewId, DAMAGE_REPORT_PARTNER, placement)

  return (
    <a
      className={className}
      href={damageReportUrl(report.licensePlateCompact)}
      target="_blank"
      rel="sponsored noopener"
      onClick={recordClick}
      onAuxClick={(event) => {
        if (event.button === 1) {
          recordClick()
        }
      }}
    >
      {children}
    </a>
  )
}

/**
 * The button's words, with the plate kept on one line. A plate breaks at its dashes like any
 * hyphenated word, and on a phone "VPF-" and "72-R" then land on separate lines. One wrapping span
 * around everything, because the button is a flex container and a bare text node beside the plate's
 * span would become two flex items with the button's gap between them.
 */
function DamageCtaText({ plate }: { plate: string }) {
  return (
    <span>
      Bekijk het schadeverleden van <span className="damage__plate">{plate}</span>
    </span>
  )
}
